import ActionMenuMore from "assets/img/svg/actionMenuMore";
import type { TImportDataDictionary } from "components/ImportData/type";
import LoadingDots from "components/LoadingDots";
import StyledButton from "components/shared/StyledButton";
import { getImportTypeTranslation } from "containers/BulkUpdatesContainer";
import type { TPendingImport } from "containers/BulkUpdatesContainer/ConfirmationStep";
import ScopeContainer from "containers/ScopeContext";
import { useKeyPress } from "hooks/useKeyPress";
import type { ReactElement } from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Job } from "store/jobsStore";

import PopoverMenu, { whiteButton } from "../PopoverMenu";

export interface PopoverMenuElements {
    [key: string]: {
        label: string;
        icon: ReactElement;
        action: () => void;
        actionDisabled?: boolean;
    }[];
}

/**
 * Styles of the header buttons. With a second button next to the menu, both become compact
 * squares (mockup size) so they fit next to "Label (count)" in the 335px list column.
 */
export const headerButtonSx = (compact: boolean) => ({
    ...whiteButton,
    minHeight: "100%",
    ...(compact ? { "&.MuiButton-root": { ...whiteButton["&.MuiButton-root"], minWidth: 36, width: 36 } } : {}),
});

interface IElementListHeader {
    label: string;
    count: number;
    /** Dots in place of "(0)" while the first page is still loading. */
    loading?: boolean;
    /** Extra buttons left of the imports / exports menu button (e.g. a view toggle) */
    headerActions?: ReactElement;
    popoverMenu?: PopoverMenuElements;
    pendingJobs?: TPendingImport[] | Job[];
    /** Node listening to Escape to close the menu (the whole list, when there is one) */
    keyNode?: Node | null;
}

/**
 * "Label (count)" header with its imports / exports menu, shared by the ElementList and the
 * views replacing it (documents table) so the buttons sit at the same place everywhere.
 */
const ElementListHeader = ({
    label,
    count,
    loading = false,
    headerActions,
    popoverMenu,
    pendingJobs = [],
    keyNode,
}: IElementListHeader) => {
    const [t] = useTranslation();
    const [showPopover, setShowPopover] = useState(false);
    const noPendingJobs = pendingJobs.length === 0;

    const handleClosePopover = useCallback(() => setShowPopover(false), []);
    useEffect(() => {
        window.addEventListener("click", handleClosePopover);
        return () => window.removeEventListener("click", handleClosePopover);
    }, [handleClosePopover]);
    useKeyPress({ keys: ["Escape"], callback: handleClosePopover, node: keyNode });

    return (
        <div className="elements-list-header" onClick={(e) => e.stopPropagation()}>
            <span className="elements-list-header-title">
                {label}
                {loading ? <LoadingDots compact /> : ` (${count})`}
            </span>
            <div className="elements-list-header-buttons">
                {headerActions}
                {popoverMenu && (
                    <ScopeContainer errorProps={{ disabled: true }} extraConditions={[noPendingJobs]}>
                        {(props) => (
                            <StyledButton
                                buttonType="outlined"
                                onClick={() => setShowPopover((prev) => !prev)}
                                sx={headerButtonSx(true)}
                                tooltipText={
                                    !noPendingJobs
                                        ? t("AsyncUploads.PendingTooltip", {
                                              element: getImportTypeTranslation(
                                                  pendingJobs[0].importType as TImportDataDictionary,
                                              ),
                                          })
                                        : ""
                                }
                                {...props}
                            >
                                <div className="elements-list-menu-button">
                                    <ActionMenuMore />
                                </div>
                            </StyledButton>
                        )}
                    </ScopeContainer>
                )}
            </div>
            {popoverMenu && <PopoverMenu showPopover={showPopover} data={popoverMenu} />}
        </div>
    );
};

export default ElementListHeader;
